import firebase from "firebase/app";
import "firebase/storage";
import { Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { projectFirestore } from "../../../../firebase-config/config";
import { useAuthContext } from "../../../../firebase-files/hooks/useAuthContext";
import { useFirestore } from "../../../../firebase-files/hooks/useFirestore";
import { useDocument } from "../../../../firebase-files/hooks/useDocument";
import { useCollection } from "../../../../firebase-files/hooks/useCollection";
import "./PlantationTwoVideo.scss";

const PlantationTwoVideo = () => {
  const wrapperRef = useRef(null);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [uploadType, setUploadType] = useState("url");
  const [formData, setFormData] = useState({
    videoUrl: "",
    title: "",
    subHeading: "",
    categories: "",
    platform: "",
    file: null,
    docId: "",
    status: true,
  });
  const [previewUrl, setPreviewUrl] = useState("");
  const [touched, setTouched] = useState({});
  const [videoData, setVideoData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const { addDocument, deleteDocument } = useFirestore("videos");
  const { user } = useAuthContext();
  const { document: appTypeDoc } = useDocument("settings", "AppType");
  const { documents: users } = useCollection("users");
  const instagramScriptRef = useRef(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    show: false,
    id: null,
  });
  const [appType, setAppType] = useState("lite");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // ✅ check if current user is admin or superAdmin
  useEffect(() => {
    if (!user || !users) {
      setIsAdmin(false);
      setIsSuperAdmin(false);
      return;
    }

    const phone = user?.phoneNumber?.replace("+91", "91");
    const userInfo = users.find((u) => u.id === phone);

    if (userInfo) {
      setIsAdmin(userInfo.role?.toLowerCase() === "admin");
      setIsSuperAdmin(userInfo.role?.toLowerCase() === "superadmin");
    } else {
      setIsAdmin(false);
      setIsSuperAdmin(false);
    }
  }, [user, users]);

  // ✅ Get apptype from appTypeDoc
  useEffect(() => {
    if (appTypeDoc) {
      // Check for both AppType and apptype fields
      if (appTypeDoc.AppType) {
        setAppType(appTypeDoc.AppType);
      } else if (appTypeDoc.apptype) {
        setAppType(appTypeDoc.apptype);
      }
    }
    setLoading(false);
  }, [appTypeDoc]);

  useEffect(() => {
    const unsubscribe = projectFirestore
      .collection("videos")
      .onSnapshot((snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          status: doc.data().status !== false,
        }));
        setVideoData(data);
      });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const hasInstagramVideos = videoData.some(
      (video) => video.platform === "Instagram"
    );
    if (hasInstagramVideos && !instagramScriptRef.current) {
      const script = document.createElement("script");
      script.src = "https://www.instagram.com/embed.js";
      script.async = true;
      script.onload = () => {
        if (window.instgrm) window.instgrm.Embeds.process();
      };
      document.body.appendChild(script);
      instagramScriptRef.current = script;
    }
    return () => {
      if (instagramScriptRef.current) {
        document.body.removeChild(instagramScriptRef.current);
      }
      instagramScriptRef.current = null;
    };
  }, [videoData]);

  const scrollLeft = () =>
    wrapperRef.current?.scrollBy({ left: -300, behavior: "smooth" });
  const scrollRight = () =>
    wrapperRef.current?.scrollBy({ left: 300, behavior: "smooth" });

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch {
      return false;
    }
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    const updatedValue = type === "file" ? files[0] : value;
    setFormData((prev) => ({ ...prev, [name]: updatedValue }));
    setTouched((prev) => ({ ...prev, [name]: true }));
    if (name === "file" && files[0]) {
      setPreviewUrl(URL.createObjectURL(files[0]));
    }
  };

  const isFieldValid = (field) => {
    if (uploadType === "url" && field === "videoUrl")
      return isValidUrl(formData.videoUrl);
    return formData[field]?.toString().trim() !== "";
  };

  const renderError = (field) => {
    if (!touched[field] || isFieldValid(field)) return null;
    if (field === "videoUrl")
      return <span className="error-message">Please enter a valid URL</span>;
    return <span className="error-message">This field is required</span>;
  };

  const isFormValid = () => {
    const required = ["categories", "platform"];
    const specific = uploadType === "url" ? "videoUrl" : "file";
    return [...required, specific, "docId"].every(isFieldValid);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;

    try {
      let videoUrl = formData.videoUrl;
      let fileName = "";

      const videoUpdate = {
        videoUrl,
        title: formData.title,
        subHeading: formData.subHeading || "",
        categories: formData.categories,
        platform: formData.platform,
        fileName,
        status: formData.status,
        views: 0,
        createdBy: user?.uid || null,
        createdAt: formData.createdAt || new Date(),
        lastUpdated: new Date(),
      };

      await projectFirestore
        .collection("videos")
        .doc(formData.docId)
        .update(videoUpdate);

      setFormData({
        videoUrl: "",
        title: "",
        subHeading: "",
        categories: "",
        platform: "",
        file: null,
        docId: "",
        status: true,
      });
      setPreviewUrl("");
      setTouched({});
      setShowModal(false);
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation.id) return;

    try {
      const docRef = projectFirestore
        .collection("videos")
        .doc(deleteConfirmation.id);
      const docSnap = await docRef.get();

      if (!docSnap.exists) {
        alert("Video not found.");
        return;
      }

      const videoData = docSnap.data();

      if (videoData.platform === "Local" && videoData.fileName) {
        const storageRef = firebase
          .storage()
          .ref(`videos/${deleteConfirmation.id}`);
        await storageRef.listAll().then((dir) => {
          const deletePromises = dir.items.map((fileRef) => fileRef.delete());
          return Promise.all(deletePromises);
        });
      }

      await deleteDocument(deleteConfirmation.id);
    } catch (error) {
      console.error("Error deleting video:", error);
      alert("Failed to delete video.");
    } finally {
      setDeleteConfirmation({ show: false, id: null });
    }
  };

  const handleEdit = (video) => {
    setFormData({
      videoUrl: video.videoUrl,
      title: video.title,
      subHeading: video.subHeading || "",
      categories: video.categories,
      platform: video.platform,
      file: null,
      docId: video.id,
      status: video.status !== undefined ? video.status : true,
      createdAt: video.createdAt,
    });
    setUploadType(video.platform === "Local" ? "upload" : "url");
    setPreviewUrl(video.platform === "Local" ? video.videoUrl : "");
    setTouched({});
    setShowModal(true);
  };

  const getInstagramPostId = (url) => {
    const regex =
      /(?:https?:\/\/)?(?:www\.)?instagram\.com(?:\/[^\/]+)?\/(p|reel)\/([^\/?#&]+)/;
    const match = url.match(regex);
    return match ? match[2] : "";
  };

  const renderVideoEmbed = (video) => {
    if (video.platform === "YouTube") {
      const embedUrl = video.videoUrl.includes("/embed/")
        ? video.videoUrl
        : video.videoUrl
            .replace("/watch?v=", "/embed/")
            .replace("/shorts/", "/embed/");
      return (
        <iframe
          src={embedUrl}
          width="100%"
          height="100%"
          allow="autoplay; encrypted-media"
          allowFullScreen
          title={video.title}
        />
      );
    } else if (video.platform === "Instagram") {
      const postId = getInstagramPostId(video.videoUrl);
      if (!postId)
        return <div className="instagram-error">Invalid Instagram URL</div>;
      return (
        <div className="instagram-embed-container">
          <blockquote
            className="instagram-media"
            data-instgrm-permalink={`https://www.instagram.com/p/${postId}/`}
            data-instgrm-version="14"
            style={{ width: "100%", height: "100%" }}
          >
            <a href={`https://www.instagram.com/p/${postId}/`}>
              View on Instagram
            </a>
          </blockquote>
        </div>
      );
    }

    return (
      <video
        src={video.videoUrl}
        width="100%"
        height="100%"
        controls
        style={{ objectFit: "contain" }}
        preload="none"
        playsInline
      />
    );
  };

  const getFilteredVideos = () => {
    let filtered = [...videoData];
    if (selectedCategory !== "All") {
      filtered = filtered.filter((v) => v.categories === selectedCategory);
    }
    if (!isAdmin && !isSuperAdmin) {
      filtered = filtered.filter((v) => v.status);
    }
    return filtered;
  };

  const uniqueCategories = [
    "All",
    ...new Set(videoData.map((v) => v.categories).filter(Boolean)),
  ];

  // Check if user can add videos based on app type and role
  const canAddVideos = () => {
    if (!user) return false;

    if (appType === "pro") {
      return isAdmin || isSuperAdmin;
    } else {
      return isSuperAdmin;
    }
  };

  // Check if user can delete videos based on app type and role
  const canDeleteVideos = () => {
    if (!user) return false;
    return isSuperAdmin;
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <section className="video-section">
      <h2>Joyful Gifting Stories</h2>

      <div className="category-filter">
        {uniqueCategories.map((cat) => (
          <button
            key={cat}
            className={cat === selectedCategory ? "active" : ""}
            onClick={() => setSelectedCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      <button className="arrow-button left" onClick={scrollLeft}>
        ❮
      </button>
      <button className="arrow-button right" onClick={scrollRight}>
        ❯
      </button>

      <div className="video-reel-wrapper" ref={wrapperRef}>
        {getFilteredVideos()
          .slice(0, 10)
          .map((video) => (
            <div
              className={`reel-card ${!video.status ? "inactive-video" : ""}`}
              key={video.id}
            >
              {(isAdmin || isSuperAdmin) && !video.status && (
                <div className="inactive-badge">Inactive</div>
              )}

              <div className="thumbnail-container">
                <div className="video-embed-wrapper">
                  {renderVideoEmbed(video)}
                </div>

                {(isAdmin || isSuperAdmin) && (
                  <>
                    {canAddVideos() && (
                      <div
                        className="edit-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(video);
                        }}
                      >
                        ✏️
                      </div>
                    )}

                    {canDeleteVideos() && (
                      <div
                        className="delete-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmation({ show: true, id: video.id });
                        }}
                      >
                        <Trash2 />
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="video-info">
                <h4>{video.title}</h4>
                <p className="subheading">{video.subHeading}</p>
              </div>
            </div>
          ))}
      </div>

      {getFilteredVideos().length > 10 && (
        <div className="show-more-container">
          <button className="show-more-btn" onClick={() => navigate("/videos")}>
            View All
          </button>
        </div>
      )}

      {canAddVideos() && (
        <div className="add-video-btn-wrapper">
          <button
            className="add-video-btn"
            onClick={() => {
              const newDocRef = projectFirestore.collection("videos").doc();
              setFormData((prev) => ({
                ...prev,
                docId: newDocRef.id,
                status: true,
              }));
              newDocRef.set({
                createdAt: new Date(),
                status: true,
              });
              setShowModal(true);
            }}
          >
            Add Video
          </button>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>
              {formData?.docId && videoData.find((v) => v.id === formData.docId)
                ? "Edit Video"
                : "Add New Video"}
            </h3>

            <div className="toggle-upload-type">
              <button
                onClick={() => setUploadType("url")}
                className={uploadType === "url" ? "active" : ""}
              >
                Video URL
              </button>
            </div>

            <form className="video-form" onSubmit={handleSubmit}>
              {uploadType === "url" && (
                <label
                  className={
                    touched.videoUrl && !isFieldValid("videoUrl") ? "error" : ""
                  }
                >
                  Video URL:
                  <input
                    type="text"
                    name="videoUrl"
                    placeholder="Enter video URL"
                    value={formData.videoUrl}
                    onChange={handleChange}
                  />
                  {renderError("videoUrl")}
                </label>
              )}

              <label
              // className={
              //   touched.title && !isFieldValid("title") ? "error" : ""
              // }
              >
                Title:
                <input
                  type="text"
                  name="title"
                  placeholder="Enter title"
                  value={formData.title}
                  onChange={handleChange}
                />
                {/* {renderError("title")} */}
              </label>

              <label>
                Sub Heading:
                <input
                  type="text"
                  name="subHeading"
                  placeholder="Enter sub heading"
                  value={formData.subHeading}
                  onChange={handleChange}
                />
              </label>

              <label
                className={
                  touched.categories && !isFieldValid("categories")
                    ? "error"
                    : ""
                }
              >
                Categories:
                <input
                  type="text"
                  name="categories"
                  placeholder="Enter categories"
                  value={formData.categories}
                  onChange={handleChange}
                />
                {renderError("categories")}
              </label>

              <label
                className={
                  touched.platform && !isFieldValid("platform") ? "error" : ""
                }
              >
                Platform:
                <select
                  name="platform"
                  value={formData.platform}
                  onChange={handleChange}
                >
                  <option value="">Select Platform</option>
                  <option value="YouTube">YouTube</option>
                  <option value="Instagram">Instagram</option>
                  <option value="Facebook">Facebook</option>
                  <option value="Local">Uploaded Video</option>
                </select>
                {renderError("platform")}
              </label>

              <label>
                Status:
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    name="status"
                    checked={formData.status}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        status: e.target.checked,
                      })
                    }
                    id="status-toggle"
                  />
                  <label
                    htmlFor="status-toggle"
                    className="toggle-slider"
                  ></label>
                  <span
                    style={{
                      color: formData.status ? "#1f4f2c" : "#ff6b6b",
                      fontWeight: "bold",
                    }}
                  >
                    {formData.status ? "Active" : "Inactive"}
                  </span>
                </div>
              </label>

              <div className="form-buttons">
                <button
                  type="submit"
                  className={!isFormValid() ? "disabled" : ""}
                  disabled={!isFormValid()}
                >
                  Submit
                </button>
                <button type="button" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirmation.show && (
        <div className="modal-overlay">
          <div className="modal-content delete-confirmation">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this video?</p>
            <div className="confirmation-buttons">
              <button className="delete-btn" onClick={confirmDelete}>
                Delete
              </button>
              <button
                className="cancel-btn"
                onClick={() => setDeleteConfirmation({ show: false, id: null })}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PlantationTwoVideo;
